import React, { Component } from 'react';
import Button from '../elements-buttons/Button';
import { getWebEnv } from '../../../api/api-env';
import Payments from '../../../api/api-payments';
import SharedIntlProviderHOC from 'shared-components/hocs/sharedIntlProviderHOC';
import { injectIntl, FormattedMessage, defineMessages } from 'react-intl';
import { get } from 'lodash';
import { getStripePublicKey } from '../../../lib/stripe_key_util';

// In some cases we care about the type of error, i.e. consent declined
const ERRORS =
  defineMessages({
    session_cancelled: {
      id: "booking_request.identity_verification.session_cancelled",
      defaultMessage: 'This reservation requires identity verification to be completed. Please try again in order to book the space.'
    },
    consent_declined: {
      id: "booking_request.identity_verification.consent_declined",
      defaultMessage: 'This reservation requires identity verification to be completed. Please try again in order to book the space.'
    },
    under_supported_age: {
      id: "booking_request.identity_verification.under_supported_age",
      defaultMessage: 'You do not meet the age requirement to book this space.'
    },
    retry_not_allowed: {
      id: "booking_request.identity_verification.retry_not_allowed",
      defaultMessage: 'Your previous verification attempt failed, please contact our support team {link} for assistance.'
    }
  });

class IdentityVerification extends Component {
  constructor (props) {
    super(props);

    this.onClick = this.onClick.bind(this);
    this.pollIdentityStatus = this.pollIdentityStatus.bind(this);

    this.state = {
      loadingModal: false,
      isSubmitting: false,
      error: false,
      ticks: 0,
      delay: 2000,
      verificationSuccess: false,
      verificationFailure: false,
      errorMessage: null,
      retry: true
    };
  }

  componentDidMount () {
    const locale = SharedIntlProviderHOC.getLocale();
    const { ssoId, accessToken } = this.props;

    getWebEnv(locale,
      (env) => {
        const stripeKeys = get(env, 'stripe', {});
        const stripe = Stripe(getStripePublicKey(this.props.country, stripeKeys));

        this.setState({
          stripe
        });

        Payments.getIdentitySession(ssoId, accessToken, (existingSession) => {
          if (existingSession && existingSession.session_id) {
            const { retry } = existingSession;
            const canRetry = retry !== undefined && retry === true;

            this.setState({
              retry: canRetry,
              // If user cannot retry, then they have previously failed due to a non-retry failure.
              ...(!retry && { verificationFailure: true }),
              ...(!retry && { errorMessage: ERRORS['retry_not_allowed'] })
            });
          }
        });
      });
  }

  componentDidUpdate (prevProps, prevState) {
    const { isSubmitting, ticks } = this.state;

    // Submitting is done, stop polling.
    if (!isSubmitting) {
      clearInterval(this.interval)
    }
  }

  pollIdentityStatus () {
    const { ticks, delay } = this.state;
    const { ssoId, accessToken, getIdentityValue, triggerId, alcoholConsumed, schedule } = this.props;

    return Payments.getIdentitySession(ssoId, accessToken, (session) => {
      const { verification_status, failure_code, retry } = session;
      const time_spent_polling = (ticks + 1) * (delay / 1000);
      // Keep track of each tick
      this.setState({
        ticks: ticks + 1
      });

      getIdentityValue(verification_status)

      if (time_spent_polling >= 30) {
        return this.setState({
          verificationFailure: true,
          verificationSuccess: false,
          isSubmitting: false,
          ticks: 0,
          errorMessage: failure_code ? ERRORS[failure_code] : null,
        })
      }

      if (verification_status === 'verified') {
        analyticsIntegrations.statusUpdateIdentityVerification({ status: verification_status, listing_id: triggerId, alcohol_consumed: alcoholConsumed, schedule });

        return this.setState({
          verificationSuccess: true,
          verificationFailure: false,
          isSubmitting: false,
          ticks: 0,
          errorMessage: null
        });
      }

      if (verification_status === 'failed') {
        analyticsIntegrations.statusUpdateIdentityVerification({ status: verification_status, listing_id: triggerId, alcohol_consumed: alcoholConsumed, schedule });

        return this.setState({
          verificationFailure: true,
          verificationSuccess: false,
          isSubmitting: false,
          ticks: 0,
          errorMessage: failure_code ? ERRORS[failure_code] : null,
          retry
        });
      }
    });
  }

  onClick () {
    const { ssoId, accessToken, triggerType, triggerId, alcoholConsumed, analyticsOnClick, metro, country, schedule } = this.props;
    const { stripe, delay } = this.state;
    const alcohol = alcoholConsumed === 'true';

    analyticsOnClick();

    this.setState({ isSubmitting: true });

    const payload = {
      trigger_type: triggerType,
      trigger_id: triggerId,
      alcohol,
      metro,
      country,
      schedule
    };

    return Payments.createIdentitySession(ssoId, accessToken, payload, (session) => {
      return stripe.verifyIdentity(session.client_secret)
        .then(result => {
          const { error } = result;

          if (error) {
            const { code } = error;
            const errorMessage = ERRORS[code];

            return this.setState({
              error,
              errorMessage,
              isSubmitting: false
            });
          }

          // IDV is submitted, start polling for updates
          this.interval = setInterval(this.pollIdentityStatus, delay);
        })
        .catch(error => {
          return this.setState({ error, isSubmitting: false });
        });
    });
  }

  render () {
    const { loadingModal, isSubmitting, verificationSuccess, verificationFailure, errorMessage, retry } = this.state;
    const { meta, input, analyticsOnClick } = this.props;
    const { submitFailed, invalid, error } = meta;
    const { name } = input;
    const zendeskLocale = SharedIntlProviderHOC.getZendeskLocale();

    if (verificationSuccess) {
      return (
        <div className="flex middle h6 no-top-margin text-gray-dark">
          <img className="icon margin-md-right" src="https://assets.peerspace.com/blue-verified.svg?v=rb" alt="Verification Icon" />
          <span>
            <FormattedMessage id="booking_request.identity_verification.verify_button" defaultMessage="Verified" />
          </span>
        </div>
      );
    }

    if (verificationFailure) {
      return (
        <div>
          <div className="flex">
            { retry &&
              <Button
                type="button"
                className="btn btn-secondary classic margin-lg-right"
                onClick={() => this.onClick()}
                disabled={isSubmitting}
                isLoading={isSubmitting}>
                  <FormattedMessage id="booking_request.identity_verification.try_again_button" defaultMessage="Try Again" />
              </Button>
            }
            <Button type="button" className="btn btn-default classic" onClick={() => analyticsOnClick()}>
              <a target="_blank" href={`https://support.peerspace.com/hc/${zendeskLocale}/requests/new`} className="btn-link-inherit-color">
                <FormattedMessage id="booking_request.identity_verification.contact_support_button" defaultMessage="Contact Support" />
              </a>
            </Button>
          </div>
          <p className="error text-danger text-small margin-lg-top margin-xl-right">
            { errorMessage
              ? <FormattedMessage
                  {...errorMessage}
                  values={{
                    link: <a target="_blank" className="text-danger underline" href={`https://support.peerspace.com/hc/${zendeskLocale}/requests/new`}>
                            <FormattedMessage
                              id="booking_request.identity_verification.here"
                              defaultMessage="here"
                            />
                          </a>
                    }}
                />
                  : <FormattedMessage
                  id="booking_request.identity_verification.failure_error_message"
                  defaultMessage='We were unable to verify your identity. Please try again ensuring you are taking a live image of your document and selfie in a well-lit area, free from glare, and on a dark background.'
                />
            }
          </p>
        </div>
      );
    }

    return (
      <div>
        <Button
          attributes={{ name }}
          type="button"
          className="btn btn-secondary classic"
          onClick={() => this.onClick()}
          disabled={isSubmitting}
          isLoading={isSubmitting}>
            <FormattedMessage id="booking_request.identity_verification.verify_identity" defaultMessage='Verify identity' />
        </Button>
        { ( submitFailed && invalid ) &&
          <p className="error text-danger text-small margin-lg-top margin-xl-right">
            <FormattedMessage
              id="booking_request.identity_verification.field_error"
              defaultMessage='Please verify your identity.' />
          </p>}
        { errorMessage &&
          <p className="error text-danger text-small margin-lg-top margin-xl-right">
            <FormattedMessage
              {...errorMessage}
              values={{
                link:
                  <a target="_blank" className="text-danger underline" href={`https://support.peerspace.com/hc/${zendeskLocale}/requests/new`}>
                    <FormattedMessage
                      id="booking_request.identity_verification.here"
                      defaultMessage="here"
                    />
                  </a>
              }}
            />
          </p>}
      </div>
    );
  }
}

exports.IdentityVerification = injectIntl(IdentityVerification);
